import { Building2, CreditCard, FileSignature, Send, Wallet } from "lucide-react";
import type { SetupStepId } from "@/hooks/use-setup-guide";

/**
 * Copy and destinations for the setup checklist, shared by the floating
 * checklist (SetupGuide) and Help & Support → Setup Guide. Which items a plan
 * gets, and whether each is done, comes from the server.
 */
export interface ChecklistItemConfig {
  id: SetupStepId;
  title: string;
  description: string;
  /** Where to go to do it yourself. A `#section-…` hash opens that Settings section. */
  href: string;
  icon: React.ElementType;
}

export const CHECKLIST_ITEMS: Record<SetupStepId, ChecklistItemConfig> = {
  "business-details": {
    id: "business-details",
    title: "Add your business details",
    description: "Your name and contact details go on every quote and invoice.",
    href: "/settings#section-business",
    icon: Building2,
  },
  "create-first-quote": {
    id: "create-first-quote",
    title: "Create your first quote",
    description: "Add a line item and see your profit before you send it.",
    href: "/quotes/new",
    icon: FileSignature,
  },
  "send-quote": {
    id: "send-quote",
    title: "Send a quote",
    description: "Email it to your customer with your branded PDF attached.",
    href: "/quotes",
    icon: Send,
  },
  "get-paid": {
    id: "get-paid",
    title: "Get paid",
    description: "Turn a quote into an invoice and record a payment.",
    href: "/invoices",
    icon: Wallet,
  },
  "accept-payments": {
    id: "accept-payments",
    title: "Accept card payments",
    description: "Connect Stripe so customers can pay your invoices online.",
    href: "/settings#section-accept-payments",
    icon: CreditCard,
  },
};

/**
 * Go to an item's page. Settings sections open from the URL hash, which has
 * to be set after the page mounts for its listener to see it.
 */
export function goToChecklistItem(href: string, navigate: (to: string) => void) {
  const [base, hash] = href.split("#");
  navigate(base);
  if (hash) {
    window.setTimeout(() => {
      if (window.location.hash !== `#${hash}`) window.location.hash = hash;
      else window.dispatchEvent(new HashChangeEvent("hashchange"));
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
  }
}
